import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  getWeekColumnData,
  invertColor,
  isCurrentWeek,
  milestoneValueDetail,
} from "../../../../../utils/annual-planner-helpers";
import { MILESTONE_KIND_TO_ICON_KEY } from "./MilestoneKindIcon";

// Replaces EventCounter (events-only) - same per-week countdown grid,
// generalized to any milestone (event or dated goal, see resolveMilestones).
// The one real behavior change: the target week itself (weekDifference === 0)
// shows the milestone's value pill ("100x3", "82.5 Kg") instead of staying
// hidden like every other non-positive week - a PR/bodyweight goal's whole
// point is that value, an event has none and stays hidden same as before.
export const MilestoneCounter = ({
  month,
  startingCalendarDay,
  numberOfWeeks,
  milestones,
  units,
}) => {
  const { t } = useTranslation("athlete-portal");

  // weekFirstDay only depends on (month, startingCalendarDay, weekNumber) -
  // computed once per week here instead of once per (milestone, week) pair
  // below, and nowUtc only needs to exist once for the whole grid, not once
  // per cell (building a moment per row is the hottest cost in this view -
  // see StatsCard.js's own comment on the same issue).
  const weekFirstDays = Array.from({ length: numberOfWeeks }, (_, i) =>
    getWeekColumnData(month, startingCalendarDay, i)
  );
  const nowUtc = moment.utc();

  // Milestones is row-per-milestone with weeks nested inside (unlike Phase's
  // week-per-column), so there's no single box per week spanning every
  // milestone row for a border to wrap - marking each row's own slot
  // .current-week instead draws a separate, gap-broken segment per row. This
  // overlay is one absolutely-positioned box instead, drawn once over every
  // row, so it reads as a single continuous column like Phase's.
  const currentWeekIndex = weekFirstDays.findIndex((weekFirstDay) =>
    isCurrentWeek(weekFirstDay)
  );

  // The value pill only ever grows rightward from its own target week (see
  // event-counter-item.value's own comment) - weekDifference === 0 alone
  // missed the case where the current week sits one or more slots AFTER the
  // target (the pill's own countdown already turned negative/hidden there),
  // since the pill can still visually reach that far right even though it's
  // a different week index. <= 0 covers both "is" and "already passed".
  const hasValueAtCurrentWeek =
    currentWeekIndex !== -1 &&
    milestones.some((milestone) => {
      const milestoneDate = moment(String(milestone.utcDate), "YYYYMMDD");
      const weekDifference = Math.floor(
        milestoneDate.diff(weekFirstDays[currentWeekIndex], "days") / 7
      );
      return (
        weekDifference <= 0 && milestoneValueDetail(milestone, units, t) != null
      );
    });

  return (
    <div className="event-counters-wrapper">
      {milestones.map((milestone, index) => {
        // Parsing "YYYYMMDD" is the slow part of building a moment - hoisted
        // to once per milestone instead of once per (milestone, week) pair.
        const milestoneDate = moment(String(milestone.utcDate), "YYYYMMDD");

        return (
          // For each milestone we map a row containing the bubbles that count the remaining weeks until it
          <div className="event-counter-row" key={`${milestone.kind}-${index}`}>
            {weekFirstDays.map((weekFirstDay, weekNumber) => {
              // Same day-diff/floor convention as bucketByWeek/useWeekSelection -
              // .diff(..., "weeks") truncates toward zero, which double-counts
              // weekDifference === 0 for two consecutive weeks whenever the
              // milestone's date isn't exactly a week-start day (the normal
              // case, since nothing picks target dates around
              // startingCalendarDay).
              const weekDifference = Math.floor(
                milestoneDate.diff(weekFirstDay, "days") / 7
              );
              const upcomingWeek = weekFirstDay.isAfter(nowUtc);

              return (
                <div
                  key={`milestone-week-${weekNumber}`}
                  className="event-counter-slot"
                >
                  <MilestoneWeekCell
                    weekDifference={weekDifference}
                    upcomingWeek={upcomingWeek}
                    milestone={milestone}
                    units={units}
                    t={t}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
      {currentWeekIndex !== -1 && !hasValueAtCurrentWeek && (
        <div
          className="milestones-current-week-marker"
          style={{
            "--milestone-week-count": numberOfWeeks,
            "--milestone-week-index": currentWeekIndex,
          }}
        />
      )}
    </div>
  );
};

const MilestoneWeekCell = ({
  weekDifference,
  upcomingWeek,
  milestone,
  units,
  t,
}) => {
  const contrastColor = invertColor(milestone.color || "#000000");

  if (weekDifference === 0) {
    const value = milestoneValueDetail(milestone, units, t);
    if (!value) return <div className="event-counter-item hidden" />;
    // Colored by milestone kind (events/goals/personalRecords - same 3
    // colors as MilestoneLegend/MilestoneKindIcon), not milestone.color -
    // that field is null for both kinds that ever reach this branch
    // (resolveMilestones only gives bodyWeight/personalRecord goals a real
    // color never; only events have one, and events never have a value).
    return (
      <div
        className={`event-counter-item value ${MILESTONE_KIND_TO_ICON_KEY[milestone.kind]}`}
      >
        {value}
      </div>
    );
  }

  if (weekDifference < 0)
    return <div className="event-counter-item hidden">{weekDifference}</div>;

  if (upcomingWeek && milestone.color)
    return (
      <div
        style={{ backgroundColor: milestone.color, color: contrastColor }}
        className="event-counter-item"
      >
        {weekDifference}
      </div>
    );
  return <div className="event-counter-item">{weekDifference}</div>;
};
